import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { Alert, Badge, Button, Card, Col, Form, Spinner, Table } from 'react-bootstrap';
import ConfirmDialog from './components/ConfirmDialog';
import Loading from './components/Loading';
import useAdminData, { responseError } from './useAdminData';
import { adminFetch, AdminAuthError } from './adminAuth';
import { formatDateTime, formatMoney } from './format';

const POLL_INTERVAL_MS = 2000;
const STATUS_VARIANTS = { completed: 'success', failed: 'danger', in_progress: 'info' };
const STATUS_LABELS = { completed: 'Completed', failed: 'Failed', in_progress: 'Running' };

/** An ended session goes back to sign in, like the other admin pages */
function useSignInAgain() {
  const history = useHistory();
  return useCallback(err => {
    if (err instanceof AdminAuthError) {
      history.replace('/admin/login');
      return true;
    }
    return false;
  }, [history]);
}

/** "$100.00 sells for $80.00 on Sunday; Square records $80.00 as its unit cost" */
export function describeSettings(settings) {
  const salePrice = 100 * (100 - Number(settings.discountPercent)) / 100;
  const unitCost = 100 * Number(settings.unitCostPercent) / 100;
  return `A ${formatMoney(100)} item sells for ${formatMoney(salePrice)} after the Sunday discount, and Square records ${formatMoney(unitCost)} as its unit cost.`;
}

export function validateSettings(values) {
  const errors = {};
  // Number(' ') is 0, so a field left with only spaces must count as empty
  const number = value => (value === null || value === undefined || String(value).trim() === '' ? NaN : Number(value));
  const discount = number(values.discountPercent);
  if (!(discount > 0 && discount < 100)) {
    errors.discountPercent = 'More than 0 and less than 100';
  }
  const unitCost = number(values.unitCostPercent);
  if (!(unitCost >= 0 && unitCost <= 100)) {
    errors.unitCostPercent = 'From 0 to 100';
  }
  if (!values.taxName || !values.taxName.trim()) {
    errors.taxName = 'Required';
  }
  const rate = number(values.taxRatePercent);
  if (!(rate > 0 && rate < 20)) {
    errors.taxRatePercent = 'More than 0 and less than 20';
  }
  return errors;
}

function ConnectionCard() {
  const { data, error, loading, reload } = useAdminData('/admin/square/connection');

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex align-items-center">
        Connection
        <Button variant="outline-secondary" size="sm" className="ml-auto" onClick={reload} disabled={loading}>
          {loading ? 'Checking...' : 'Check again'}
        </Button>
      </Card.Header>
      <Card.Body>
        {loading && !data && <div className="text-muted"><Spinner animation="border" size="sm" className="mr-2" />Checking with Square...</div>}
        {error && <Alert variant="danger" className="mb-0">{error}</Alert>}
        {data && (
          <>
            {data.connected ? (
              <p className="mb-2">
                Connected to the Square <strong>{data.environment}</strong> account.{' '}
                {data.locations.length > 0 && (
                  <span className="text-muted">Locations: {data.locations.map(l => `${l.name}${l.status === 'ACTIVE' ? '' : ` (${l.status.toLowerCase()})`}`).join(', ')}</span>
                )}
              </p>
            ) : (
              <Alert variant="danger" className="mb-2">{data.error || 'Not connected to Square.'}</Alert>
            )}
            {data.settingsError && <Alert variant="danger" className="mb-2">{data.settingsError}</Alert>}
            {data.otherEnabledTaxes && data.otherEnabledTaxes.length > 0 && (
              <Alert variant="warning" className="mb-2">
                Square also has {data.otherEnabledTaxes.map(tax => `"${tax.name}" (${Number(tax.percentage)}%)`).join(', ')} switched on.
                If that's an old tax the sync no longer uses, turn it off in Square so items aren't charged twice.
              </Alert>
            )}
            {data.tax && data.tax.missing && (
              <p className="mb-0 text-muted">The tax "{data.tax.name}" isn't in Square yet; the next sync creates it.</p>
            )}
            {data.tax && !data.tax.missing && data.tax.matchesSettings && (
              <p className="mb-0 text-muted">Tax "{data.tax.name}" is set to {Number(data.tax.percentage)}% in Square, matching the settings below.</p>
            )}
            {data.tax && !data.tax.missing && !data.tax.matchesSettings && (
              <Alert variant="warning" className="mb-0">
                Tax "{data.tax.name}" is {data.tax.enabled ? `${Number(data.tax.percentage)}%` : 'turned off'} in Square, which doesn't match
                the settings below. The next sync updates it.
              </Alert>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

function RunResult({ run }) {
  if (run.status === 'failed') {
    return <Alert variant="danger">The sync failed: {run.errorMessage || 'unknown error'}</Alert>;
  }
  return (
    <div>
      <Alert variant={run.failed > 0 ? 'warning' : 'success'}>
        {run.discountApplied ? 'Sunday discount sync' : 'Sync'} finished: {run.created} created, {run.updated} updated,
        {' '}{run.skipped} skipped, {run.failed} failed, of {run.totalItems} items.
      </Alert>
      {run.failures && run.failures.length > 0 && (
        <Table size="sm" bordered>
          <thead>
            <tr><th>Item #</th><th>Title</th><th>Problem</th></tr>
          </thead>
          <tbody>
            {run.failures.map(failure => (
              <tr key={`${failure.itemId}-${failure.reason}`}>
                <td>{failure.itemId}</td>
                <td>{failure.title}</td>
                <td>{failure.reason}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function SyncCard({ itemCount, discountEligibleCount, settings, runningId, onFinished }) {
  const signInAgain = useSignInAgain();
  const [confirm, setConfirm] = useState(null);
  const [starting, setStarting] = useState(false);
  const [followingId, setFollowingId] = useState(runningId);
  const [run, setRun] = useState(null);
  const [message, setMessage] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (runningId) {
      setFollowingId(runningId);
    }
  }, [runningId]);

  // Poll the run being followed until it finishes
  useEffect(() => {
    if (!followingId) {
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await adminFetch(`/admin/square/sync/${followingId}`);
        if (!response.ok) {
          throw new Error(await responseError(response));
        }
        const current = await response.json();
        if (cancelled) {
          return;
        }
        setRun(current);
        if (current.status === 'in_progress') {
          timer.current = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          setFollowingId(null);
          onFinished();
        }
      } catch (err) {
        if (cancelled || signInAgain(err)) {
          return;
        }
        setMessage({ variant: 'danger', text: `Lost track of the sync: ${err.message}` });
        setFollowingId(null);
      }
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
  }, [followingId, onFinished, signInAgain]);

  const start = async () => {
    setStarting(true);
    setMessage(null);
    setRun(null);
    try {
      const query = confirm.discount ? '?applyDiscount=true' : '';
      const response = await adminFetch(`/admin/square/sync${query}`, { method: 'POST' });
      const body = await response.json().catch(() => ({}));
      if (response.status === 409 && body.syncId) {
        setMessage({ variant: 'info', text: 'A sync was already running; showing its progress.' });
        setFollowingId(body.syncId);
      } else if (!response.ok) {
        setMessage({ variant: 'danger', text: body.error || `Request failed (${response.status})` });
      } else {
        setFollowingId(body.syncId);
      }
    } catch (err) {
      if (signInAgain(err)) {
        return;
      }
      setMessage({ variant: 'danger', text: err.message });
    }
    setStarting(false);
    setConfirm(null);
  };

  const running = !!followingId;

  return (
    <Card className="mb-3">
      <Card.Header>Sync items to Square</Card.Header>
      <Card.Body>
        <p>
          <strong>{itemCount}</strong> items, <strong>{discountEligibleCount}</strong> of them discount eligible.
          Syncing creates new items in Square and updates changed ones. Items deleted here aren't removed from
          Square; delete those in Square too.
        </p>
        {message && <Alert variant={message.variant}>{message.text}</Alert>}
        {running && (
          <Alert variant="info" className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="mr-2" />
            {run && run.discountApplied ? 'Sunday discount sync' : 'Sync'} in progress
            {run && run.startedAt ? `, started ${formatDateTime(run.startedAt)}` : ''}. This takes a minute or two.
          </Alert>
        )}
        {!running && run && <RunResult run={run} />}
        <div className="d-flex flex-wrap">
          <Button className="mr-2 mb-2" disabled={running || starting} onClick={() => setConfirm({ discount: false })}>
            Sync items
          </Button>
          <Button variant="warning" className="mr-2 mb-2" disabled={running || starting} onClick={() => setConfirm({ discount: true })}>
            Run Sunday discount sync
          </Button>
        </div>
      </Card.Body>
      <ConfirmDialog
        show={!!confirm}
        title={confirm && confirm.discount ? 'Run the Sunday discount sync?' : 'Sync items to Square?'}
        message={confirm && (confirm.discount ? (
          <>
            <p>Syncs all {itemCount} items, marking the {discountEligibleCount} discount-eligible items down {settings.discountPercent}% in Square.</p>
            <p className="mb-0">A regular sync afterwards puts them back to full price.</p>
          </>
        ) : (
          <p className="mb-0">Syncs all {itemCount} items to Square at full price, creating new items and updating changed ones.</p>
        ))}
        confirmLabel={confirm && confirm.discount ? 'Run discount sync' : 'Sync items'}
        confirmVariant={confirm && confirm.discount ? 'warning' : 'primary'}
        busy={starting}
        onConfirm={start}
        onCancel={() => setConfirm(null)}
      />
    </Card>
  );
}

function SettingsCard({ settings, settingsAreDefaults, categories, onSaved }) {
  const signInAgain = useSignInAgain();
  const [message, setMessage] = useState(null);

  const initialValues = {
    discountPercent: String(settings.discountPercent),
    unitCostPercent: String(settings.unitCostPercent),
    taxName: settings.taxName,
    taxRatePercent: String(settings.taxRatePercent),
    taxExemptCategories: settings.taxExemptCategories
  };

  const save = async (values, { setSubmitting }) => {
    setMessage(null);
    try {
      const response = await adminFetch('/admin/square/settings', {
        method: 'PUT',
        body: {
          discountPercent: Number(values.discountPercent),
          unitCostPercent: Number(values.unitCostPercent),
          taxName: values.taxName.trim(),
          taxRatePercent: Number(values.taxRatePercent),
          taxExemptCategories: values.taxExemptCategories
        }
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error((body.errors || [body.error || `Request failed (${response.status})`]).join('; '));
      }
      const result = await response.json();
      setMessage({ variant: 'success', text: `Saved. ${result.note}` });
      onSaved();
    } catch (err) {
      if (signInAgain(err)) {
        return;
      }
      setMessage({ variant: 'danger', text: err.message });
    }
    setSubmitting(false);
  };

  return (
    <Card className="mb-3">
      <Card.Header>Sync settings</Card.Header>
      <Card.Body>
        {settingsAreDefaults && <p className="text-muted">These are the standard settings; nothing has been saved yet.</p>}
        {message && <Alert variant={message.variant} onClose={() => setMessage(null)} dismissible>{message.text}</Alert>}
        <Formik initialValues={initialValues} validate={validateSettings} onSubmit={save} enableReinitialize>
          {({ values, errors, handleChange, handleSubmit, isSubmitting, dirty, setFieldValue }) => {
            const toggleCategory = category => {
              const current = values.taxExemptCategories;
              setFieldValue('taxExemptCategories', current.includes(category)
                ? current.filter(c => c !== category)
                : [...current, category]);
            };
            const percentField = (name, label) => (
              <Form.Group as={Col} sm={6} md={3} controlId={`square-${name}`}>
                <Form.Label>{label}</Form.Label>
                <Form.Control name={name} inputMode="decimal" value={values[name]} onChange={handleChange} isInvalid={!!errors[name]} />
                <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>
              </Form.Group>
            );
            return (
              <Form noValidate onSubmit={handleSubmit}>
                <Form.Row>
                  {percentField('discountPercent', 'Sunday discount (%)')}
                  {percentField('unitCostPercent', 'Unit cost (% of price)')}
                </Form.Row>
                {!errors.discountPercent && !errors.unitCostPercent && (
                  <p className="small text-muted">{describeSettings(values)}</p>
                )}
                <Form.Row>
                  <Form.Group as={Col} sm={6} controlId="square-taxName">
                    <Form.Label>Tax name in Square</Form.Label>
                    <Form.Control name="taxName" value={values.taxName} onChange={handleChange} isInvalid={!!errors.taxName} />
                    <Form.Control.Feedback type="invalid">{errors.taxName}</Form.Control.Feedback>
                    {values.taxName.trim() !== settings.taxName && !errors.taxName && (
                      <Form.Text className="text-warning">
                        The next sync creates a new tax with this name. Turn off "{settings.taxName}" in Square afterwards,
                        or items may be taxed twice.
                      </Form.Text>
                    )}
                  </Form.Group>
                  {percentField('taxRatePercent', 'Tax rate (%)')}
                </Form.Row>
                <Form.Group>
                  <Form.Label>Tax-exempt categories</Form.Label>
                  <div className="d-flex flex-wrap">
                    {categories.map(category => (
                      <Form.Check
                        key={category}
                        id={`square-exempt-${category}`}
                        className="mr-3"
                        label={category}
                        checked={values.taxExemptCategories.includes(category)}
                        onChange={() => toggleCategory(category)}
                      />
                    ))}
                  </div>
                </Form.Group>
                <Button type="submit" disabled={isSubmitting || !dirty || Object.keys(errors).length > 0}>
                  {isSubmitting ? 'Saving...' : 'Save settings'}
                </Button>
                <Form.Text muted>Changes apply from the next sync.</Form.Text>
              </Form>
            );
          }}
        </Formik>
      </Card.Body>
    </Card>
  );
}

function HistoryCard({ history }) {
  return (
    <Card>
      <Card.Header>Recent syncs</Card.Header>
      {history.length === 0 ? (
        <Card.Body className="text-muted">No syncs yet.</Card.Body>
      ) : (
        <Table size="sm" className="mb-0" responsive>
          <thead>
            <tr><th>Started</th><th>By</th><th>Type</th><th>Status</th><th>Created</th><th>Updated</th><th>Skipped</th><th>Failed</th></tr>
          </thead>
          <tbody>
            {history.map(run => (
              <tr key={run.id}>
                <td>{formatDateTime(run.startedAt)}</td>
                <td>{run.adminUsername}</td>
                <td>{run.discountApplied ? 'Sunday discount' : 'Regular'}</td>
                <td>
                  <Badge variant={STATUS_VARIANTS[run.status] || 'secondary'}>{STATUS_LABELS[run.status] || run.status}</Badge>
                  {run.status === 'failed' && run.errorMessage && <div className="small text-muted">{run.errorMessage}</div>}
                </td>
                <td>{run.created}</td>
                <td>{run.updated}</td>
                <td>{run.skipped}</td>
                <td>{run.failed}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

export default function SquarePage() {
  const { data, error, loading, waking, reload } = useAdminData('/admin/square');

  if (loading && !data) {
    return <Loading waking={waking} />;
  }
  if (error) {
    return <Alert variant="danger">{error} <Button variant="link" onClick={reload}>Try again</Button></Alert>;
  }

  const running = data.history.find(run => run.status === 'in_progress');

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <h2 className="mb-0 mr-3">Square</h2>
        <Badge variant={data.environment === 'production' ? 'success' : 'warning'} className="p-2">
          {data.environment === 'production' ? 'Production account' : 'Sandbox account'}
        </Badge>
      </div>
      <ConnectionCard />
      <SyncCard
        itemCount={data.itemCount}
        discountEligibleCount={data.discountEligibleCount}
        settings={data.settings}
        runningId={running ? running.id : null}
        onFinished={reload}
      />
      <SettingsCard
        settings={data.settings}
        settingsAreDefaults={data.settingsAreDefaults}
        categories={data.categories}
        onSaved={reload}
      />
      <HistoryCard history={data.history} />
    </div>
  );
}
